import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { 
  Camera, UtensilsCrossed, ArrowLeft, Sparkles, Loader2, 
  Clock, Users, Flame, Apple, Beef, Wheat, Droplets,
  ImagePlus, RefreshCw, BookOpen, Check, Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";
import GustoLoadingOverlay from "@/components/GustoLoadingOverlay";

const DishScannerPage = () => {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingRecipe, setGeneratingRecipe] = useState(false);
  const [dishAnalysis, setDishAnalysis] = useState(null);
  const [generatedRecipe, setGeneratedRecipe] = useState(null);
  const [error, setError] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const triggerCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
      cameraInputRef.current.click();
    }
  };

  const triggerGallery = () => {
    if (galleryInputRef.current) {
      galleryInputRef.current.value = '';
      galleryInputRef.current.click();
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setDishAnalysis(null);
      setGeneratedRecipe(null);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const analyzeDish = async () => {
    if (!imagePreview) return;

    setAnalyzing(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/dish/analyze`, {
        image_base64: imagePreview
      });
      
      setDishAnalysis(response.data);
      toast.success("Plat analysé avec succès !");
    } catch (err) {
      console.error("Analysis error:", err);
      setError("Erreur lors de l'analyse. Veuillez réessayer.");
      toast.error("Erreur lors de l'analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const generateRecipe = async () => {
    if (!imagePreview) return;

    setGeneratingRecipe(true);
    
    try {
      const response = await axios.post(`${API}/dish/generate-recipe`, {
        image_base64: imagePreview
      });
      
      setGeneratedRecipe(response.data);
      toast.success("Recette générée par Chef Gusto !");
    } catch (err) {
      console.error("Recipe generation error:", err);
      toast.error("Erreur lors de la génération de la recette");
    } finally {
      setGeneratingRecipe(false);
    }
  };

  const resetScan = () => {
    setImagePreview(null);
    setDishAnalysis(null);
    setGeneratedRecipe(null);
    setError(null);
  };

  const viewRecipe = () => {
    if (generatedRecipe) {
      sessionStorage.setItem('webRecipe', JSON.stringify({
        ...generatedRecipe,
        source_name: "Chef Gusto",
        is_from_web: true
      }));
      navigate(`/web-recipe/${generatedRecipe.id}`);
    }
  };

  // Calculate macro percentages for the chart
  const getMacroPercentages = (nutrition) => {
    if (!nutrition) return { proteins: 0, carbs: 0, fats: 0 };
    const total = (nutrition.proteins * 4) + (nutrition.carbs * 4) + (nutrition.fats * 9);
    return {
      proteins: Math.round((nutrition.proteins * 4 / total) * 100) || 0,
      carbs: Math.round((nutrition.carbs * 4 / total) * 100) || 0,
      fats: Math.round((nutrition.fats * 9 / total) * 100) || 0
    };
  };

  return (
    <div className="min-h-screen pb-24">
      <GustoLoadingOverlay show={analyzing} message="Chef Gusto analyse votre plat..." />
      <div className="bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-orange-500/10 px-4 pt-6 pb-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/discover"
            className="inline-flex items-center gap-2 text-charcoal/70 hover:text-charcoal mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <UtensilsCrossed className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-charcoal">
                Scanner de Plat
              </h1>
              <p className="text-sm text-muted-foreground">
                Analysez un plat pour découvrir sa composition
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 -mt-4 max-w-4xl mx-auto space-y-4">
        
        {/* Image Capture Card */}
        <Card className="shadow-lg overflow-hidden">
          <CardContent className="p-0">
            {imagePreview ? (
              <div className="relative">
                <img 
                  src={imagePreview} 
                  alt="Plat à analyser" 
                  className="w-full aspect-square object-cover"
                />
                <button
                  onClick={resetScan}
                  className="absolute top-3 right-3 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                
                {analyzing && (
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center">
                    <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
                    <p className="text-white font-medium">Analyse du plat...</p>
                    <p className="text-white/60 text-sm mt-1">Identification des ingrédients</p>
                  </div>
                )}
              </div>
            ) : (
              <div 
                className="aspect-square flex flex-col items-center justify-center bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={triggerCamera}
              >
                <div className="w-20 h-20 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
                  <UtensilsCrossed className="w-10 h-10 text-purple-600" />
                </div>
                <p className="font-medium text-charcoal">Photographier un plat</p>
                <p className="text-sm text-muted-foreground mt-1">pour analyser sa composition</p>
              </div>
            )}

            {/* Camera Input */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageChange}
              className="sr-only"
            />
            
            {/* Gallery Input */}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="sr-only"
            />
          </CardContent>
        </Card>

        {/* Action Buttons (when no image) */}
        {!imagePreview && (
          <div className="space-y-3">
            <Button
              onClick={triggerCamera}
              className="w-full h-14 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-600 text-lg gap-2"
            >
              <Camera className="w-5 h-5" />
              Prendre une photo
            </Button>
            
            <Button
              variant="outline"
              onClick={triggerGallery}
              className="w-full h-12 rounded-2xl gap-2"
            >
              <ImagePlus className="w-5 h-5" />
              Choisir depuis la galerie
            </Button>
          </div>
        )}

        {/* Analyze Button (when image is loaded) */}
        {imagePreview && !dishAnalysis && !analyzing && (
          <Button
            onClick={analyzeDish}
            className="w-full h-14 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-600 text-lg gap-2"
          >
            <Sparkles className="w-5 h-5" />
            Analyser ce plat
          </Button>
        )}

        {/* Error Message */}
        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center text-red-600">
              {error}
            </CardContent>
          </Card>
        )}

        {/* Analysis Results */}
        <AnimatePresence>
          {dishAnalysis && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* Dish Name & Confidence */}
              <Card className="shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-heading text-xl font-bold text-charcoal">
                        {dishAnalysis.dish_name}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">
                        {dishAnalysis.description}
                      </p>
                    </div>
                    <Badge 
                      className={`${
                        dishAnalysis.confidence === 'high' ? 'bg-green-500' :
                        dishAnalysis.confidence === 'medium' ? 'bg-amber-500' :
                        'bg-red-400'
                      }`}
                    >
                      {dishAnalysis.confidence === 'high' ? 'Sûr' :
                       dishAnalysis.confidence === 'medium' ? 'Probable' : 'Incertain'}
                    </Badge>
                  </div>
                  {dishAnalysis.cuisine_type && (
                    <Badge variant="outline" className="mt-2">
                      Cuisine {dishAnalysis.cuisine_type}
                    </Badge>
                  )}
                </CardContent>
              </Card>

              {/* Nutrition Card */}
              <Card className="shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 p-4">
                  <div className="flex items-center gap-2 text-white">
                    <Flame className="w-5 h-5" />
                    <span className="font-semibold">Informations Nutritionnelles</span>
                  </div>
                </div>
                <CardContent className="p-4">
                  {/* Total Calories */}
                  <div className="text-center mb-4">
                    <p className="text-4xl font-bold text-charcoal">
                      {dishAnalysis.nutrition?.total_calories || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">kcal par portion</p>
                  </div>

                  <Separator className="my-4" />

                  {/* Macros */}
                  <div className="grid grid-cols-3 gap-4">
                    {/* Proteins */}
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-2">
                        <Beef className="w-6 h-6 text-blue-600" />
                      </div>
                      <p className="text-lg font-bold text-charcoal">{dishAnalysis.nutrition?.proteins || 0}g</p>
                      <p className="text-xs text-muted-foreground">Protéines</p>
                      <Progress 
                        value={getMacroPercentages(dishAnalysis.nutrition).proteins} 
                        className="h-1.5 mt-2"
                      />
                    </div>

                    {/* Carbs */}
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-2">
                        <Wheat className="w-6 h-6 text-amber-600" />
                      </div>
                      <p className="text-lg font-bold text-charcoal">{dishAnalysis.nutrition?.carbs || 0}g</p>
                      <p className="text-xs text-muted-foreground">Glucides</p>
                      <Progress 
                        value={getMacroPercentages(dishAnalysis.nutrition).carbs} 
                        className="h-1.5 mt-2"
                      />
                    </div>

                    {/* Fats */}
                    <div className="text-center">
                      <div className="w-12 h-12 mx-auto rounded-full bg-pink-100 flex items-center justify-center mb-2">
                        <Droplets className="w-6 h-6 text-pink-600" />
                      </div>
                      <p className="text-lg font-bold text-charcoal">{dishAnalysis.nutrition?.fats || 0}g</p>
                      <p className="text-xs text-muted-foreground">Lipides</p>
                      <Progress 
                        value={getMacroPercentages(dishAnalysis.nutrition).fats} 
                        className="h-1.5 mt-2"
                      />
                    </div>
                  </div>

                  {dishAnalysis.nutrition?.fiber && (
                    <div className="mt-4 text-center text-sm text-muted-foreground">
                      <Apple className="w-4 h-4 inline mr-1" />
                      {dishAnalysis.nutrition.fiber}g de fibres
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ingredients List */}
              <Card className="shadow-lg">
                <CardContent className="p-4">
                  <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <Apple className="w-5 h-5 text-green-600" />
                    Ingrédients détectés
                    <Badge variant="outline">{dishAnalysis.ingredients?.length || 0}</Badge>
                  </h3>
                  
                  <div className="space-y-2">
                    {dishAnalysis.ingredients?.map((ing, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="font-medium">{ing.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{ing.quantity} {ing.unit}</span>
                          {ing.calories && (
                            <Badge variant="secondary" className="text-xs">
                              {ing.calories} kcal
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Generate Recipe Button */}
              {!generatedRecipe && (
                <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-charcoal">Recréer ce plat ?</h3>
                        <p className="text-sm text-muted-foreground">
                          Chef Gusto peut vous donner la recette complète
                        </p>
                      </div>
                    </div>
                    
                    <Button
                      onClick={generateRecipe}
                      disabled={generatingRecipe}
                      className="w-full h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                    >
                      {generatingRecipe ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Chef Gusto prépare la recette...
                        </>
                      ) : (
                        <>
                          <BookOpen className="w-5 h-5 mr-2" />
                          Obtenir la recette complète
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Generated Recipe Preview */}
              {generatedRecipe && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="shadow-lg border-cyan-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4">
                      <div className="flex items-center gap-2 text-white">
                        <GustoIcon className="w-5 h-5" />
                        <span className="font-semibold">Recette de Chef Gusto</span>
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-heading text-lg font-bold text-charcoal mb-2">
                        {generatedRecipe.dish_name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {generatedRecipe.description}
                      </p>

                      {/* Recipe Meta */}
                      <div className="flex flex-wrap gap-3 mb-4">
                        {generatedRecipe.prep_time && (
                          <Badge variant="outline" className="gap-1">
                            <Clock className="w-3 h-3" />
                            Prépa: {generatedRecipe.prep_time}
                          </Badge>
                        )}
                        {generatedRecipe.cook_time && (
                          <Badge variant="outline" className="gap-1">
                            <Flame className="w-3 h-3" />
                            Cuisson: {generatedRecipe.cook_time}
                          </Badge>
                        )}
                        {generatedRecipe.servings && (
                          <Badge variant="outline" className="gap-1">
                            <Users className="w-3 h-3" />
                            {generatedRecipe.servings}
                          </Badge>
                        )}
                        {generatedRecipe.difficulty && (
                          <Badge variant="secondary">
                            {generatedRecipe.difficulty}
                          </Badge>
                        )}
                      </div>

                      {/* Ingredients Count */}
                      <p className="text-sm text-muted-foreground mb-4">
                        {generatedRecipe.ingredients?.length || 0} ingrédients • {generatedRecipe.steps?.length || 0} étapes
                      </p>

                      {/* Tips Preview */}
                      {generatedRecipe.tips?.length > 0 && (
                        <div className="bg-amber-50 rounded-lg p-3 mb-4">
                          <div className="flex items-center gap-2 text-amber-700 text-sm">
                            <Info className="w-4 h-4" />
                            <span className="font-medium">Conseil du Chef:</span>
                          </div>
                          <p className="text-sm text-amber-600 mt-1">
                            {generatedRecipe.tips[0]}
                          </p>
                        </div>
                      )}

                      <Button
                        onClick={viewRecipe}
                        className="w-full h-12 rounded-xl"
                      >
                        Voir la recette complète
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {/* New Scan Button */}
              <Button
                variant="outline"
                onClick={resetScan}
                className="w-full h-12 rounded-xl gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Scanner un autre plat
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Info Card (when no image) */}
        {!imagePreview && (
          <Card className="bg-purple-50/50 border-purple-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-charcoal mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
                Comment ça marche ?
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs flex-shrink-0">1</span>
                  Prenez une photo de nimporte quel plat
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs flex-shrink-0">2</span>
                  LIA identifie les ingrédients et calcule les calories
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs flex-shrink-0">3</span>
                  Demandez à Chef Gusto de recréer la recette
                </li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DishScannerPage;
